import { flatten } from '../../../../utils';

interface FieldCombinations {
  fieldName: string;
  existList: string[];
  shouldNotExistList: string[];
}

const replaceIndexToken = (template: string, index: number): string =>
  template.replace(/\{index\}/g, index.toString());

const consumerAddressMinimumFieldTemplates = [
  'Addr{index}_House_No, Addr{index}_Street, Addr{index}_ZIP5',
  'Addr{index}_House_No, Addr{index}_Street, Addr{index}_ZIP9',
  'Addr{index}_House_No, Addr{index}_Street, Addr{index}_ZIP5_No_Zero_Fill',
  'Addr{index}_House_No, Addr{index}_Street, Addr{index}_St_ZIP5',
  'Addr{index}_House_No, Addr{index}_Street, Addr{index}_City_St_ZIP5',
  'Addr{index}_Line1, Addr{index}_ZIP5',
  'Addr{index}_Line1, Addr{index}_ZIP9',
  'Addr{index}_Line1, Addr{index}_ZIP5_No_Zero_Fill',
  'Addr{index}_Line1, Addr{index}_St_ZIP5',
  'Addr{index}_Line1, Addr{index}_City_St_ZIP5',
  'Addr{index}_Full',
];

export const consumerAddressMinimumFields: string[][] = flatten(
  Array(3)
    .fill(0)
    .map((_, i) => i + 1)
    .map((index) =>
      consumerAddressMinimumFieldTemplates.map((a) =>
        replaceIndexToken(a, index).split(/,\s+/),
      ),
    ),
);

export const consumerNameMinimumsFields: string[][] = [
  ['First_Name', 'Last_Name'],
  ['First_Middle_Name', 'Last_Name'],
  ['First_Middle_Name', 'Last_Name_Gen_Code'],
  ['First_Name', 'Last_Name_Gen_Code'],
  ['Full_Name_FML'],
  ['Full_Name_LFM'],
  ['Full_Name_FL'],
  ['Full_Name_LF'],
];

const consumerAddressCombinationsTemplates: FieldCombinations[] = [
  {
    fieldName: 'Addr{index}_ZIP5',
    existList: [] as string[],
    shouldNotExistList: ['Addr{index}_ZIP9', 'Addr{index}_ZIP5_No_Zero_Fill'],
  },
  {
    fieldName: 'Addr{index}_ZIP9',
    existList: [] as string[],
    shouldNotExistList: ['Addr{index}_ZIP5', 'Addr{index}_ZIP5_No_Zero_Fill'],
  },
  {
    fieldName: 'Addr{index}_ZIP5_No_Zero_Fill',
    existList: [] as string[],
    shouldNotExistList: ['Addr{index}_ZIP5', 'Addr{index}_ZIP9'],
  },
  {
    fieldName: 'Addr{index}_City_St',
    existList: [] as string[],
    shouldNotExistList: [
      'Addr{index}_City',
      'Addr{index}_St',
      'Addr{index}_St_ZIP5',
      'Addr{index}_City_St_ZIP5',
    ],
  },
  {
    fieldName: 'Addr{index}_St_ZIP5',
    existList: [] as string[],
    shouldNotExistList: [
      'Addr{index}_St',
      'Addr{index}_ZIP5',
      'Addr{index}_City_St',
      'Addr{index}_City_St_ZIP5',
      'Addr{index}_ZIP5_No_Zero_Fill',
      'Addr{index}_ZIP9',
    ],
  },
  {
    fieldName: 'Addr{index}_City_St_ZIP5',
    existList: [] as string[],
    shouldNotExistList: [
      'Addr{index}_City',
      'Addr{index}_St',
      'Addr{index}_City_St',
      'Addr{index}_St_ZIP5',
      'Addr{index}_ZIP5',
      'Addr{index}_ZIP5_No_Zero_Fill',
      'Addr{index}_ZIP9',
    ],
  },
  {
    fieldName: 'Addr{index}_Line1',
    existList: [] as string[],
    shouldNotExistList: ['Addr{index}_House_No', 'Addr{index}_Street'],
  },
  {
    fieldName: 'Addr{index}_Line2',
    existList: [] as string[],
    shouldNotExistList: ['Addr{index}_Apt'],
  },
  {
    fieldName: 'Addr{index}_Full',
    existList: [] as string[],
    shouldNotExistList: [
      'Addr{index}_Line1',
      'Addr{index}_House_No',
      'Addr{index}_Street',
      'Addr{index}_City',
      'Addr{index}_St',
      'Addr{index}_ZIP5',
      'Addr{index}_ZIP5_No_Zero_Fill',
      'Addr{index}_ZIP9',
    ],
  },
];

export const consumerNameCombinations: FieldCombinations[] = [
  {
    fieldName: 'First_Name',
    existList: [] as string[],
    shouldNotExistList: [
      'First_Middle_Name',
      'Full_Name_FML',
      'Full_Name_LMF',
      'Full_Name_FL',
      'Full_Name_LF',
    ],
  },
  {
    fieldName: 'Last_Name',
    existList: [] as string[],
    shouldNotExistList: [
      'Full_Name_FML',
      'Full_Name_LMF',
      'Full_Name_FL',
      'Full_Name_LF',
    ],
  },
  {
    fieldName: 'Middle_Name',
    existList: [] as string[],
    shouldNotExistList: ['First_Middle_Name', 'Full_Name_FML', 'Full_Name_LMF'],
  },
  {
    fieldName: 'First_Middle_Name',
    existList: [] as string[],
    shouldNotExistList: [
      'First_Name',
      'Middle_Name',
      'Full_Name_FML',
      'Full_Name_LMF',
      'Full_Name_FL',
      'Full_Name_LF',
    ],
  },
  ...['Full_Name_FML', 'Full_Name_LMF'].map((fieldName) => ({
    fieldName,
    existList: [] as string[],
    shouldNotExistList: [
      'First_Name',
      'First_Middle_Name',
      'Last_Name',
      'Last_Name_Gen_Code',
      'Middle_Name',
    ],
  })),

  ...['Full_Name_FL', 'Full_Name_LF'].map((fieldName) => ({
    fieldName,
    existList: [] as string[],
    shouldNotExistList: [
      'First_Name',
      'First_Middle_Name',
      'Last_Name',
      'Last_Name_Gen_Code',
    ],
  })),
].map((a) => {
  return {
    fieldName: a.fieldName.toLowerCase(),
    existList: a.existList.map((b) => b.toLowerCase()),
    shouldNotExistList: a.shouldNotExistList.map((b) => b.toLowerCase()),
  };
});
export const consumerAddressCombinations: FieldCombinations[] = flatten(
  Array(3)
    .fill(0)
    .map((_, i) => i + 1)
    .map((index) =>
      consumerAddressCombinationsTemplates.map((a) => ({
        fieldName: replaceIndexToken(a.fieldName, index).toLowerCase(),
        existList: a.existList.map((b) =>
          replaceIndexToken(b, index).toLowerCase(),
        ),
        shouldNotExistList: a.shouldNotExistList.map((b) =>
          replaceIndexToken(b, index).toLowerCase(),
        ),
      })),
    ),
);
